import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';

const cities = [
  {
    slug: '/bartender-jobs-youngstown-oh',
    city: 'Youngstown, OH',
    description: 'Explore bartender shifts, event gigs, and recurring bar roles in Youngstown.',
  },
  {
    slug: '/bartender-jobs-austin-tx',
    city: 'Austin, TX',
    description: 'Discover bartender opportunities and nightlife shifts in Austin.',
  },
  {
    slug: '/bartender-jobs-miami-fl',
    city: 'Miami, FL',
    description: 'Find event gigs and flexible bar shifts in Miami.',
  },
  {
    slug: '/bartender-jobs-chicago-il',
    city: 'Chicago, IL',
    description: 'Explore high-volume bar shifts and cocktail roles across Chicago neighborhoods.',
  },
  {
    slug: '/bartender-jobs-nashville-tn',
    city: 'Nashville, TN',
    description: 'Discover nightlife, live-music venue, and event bartending opportunities in Nashville.',
  },
  {
    slug: '/bartender-jobs-las-vegas-nv',
    city: 'Las Vegas, NV',
    description: 'Find hospitality, lounge, and event bartending gigs in Las Vegas.',
  },
];

const faqs = [
  {
    question: 'Is BarsConnect free for bartenders?',
    answer: 'Yes. Creating a bartender profile and using the platform is free during the current beta period. No credit card is required.',
  },
  {
    question: 'What kind of jobs can I find on BarsConnect?',
    answer: 'BarsConnect supports full-time, part-time, and event bartending opportunities. You can set your profile to reflect the type of work you are looking for.',
  },
  {
    question: 'How do bars contact me?',
    answer: 'Bar owners with accounts on BarsConnect can view your profile and send you a message directly through the platform messaging system.',
  },
  {
    question: 'Do I need to apply for jobs?',
    answer: 'Your profile is visible to bar owners who are actively browsing for bartenders. They can reach out to you based on your profile details.',
  },
  {
    question: 'What is the Verified Bartender badge?',
    answer: 'Some bartender profiles may show a Verified Bartender designation on the platform.',
  },
  {
    question: 'Can I use BarsConnect if I am only available on weekends?',
    answer: 'Yes. Your profile lets you indicate your availability so bars looking for weekend coverage can find you.',
  },
];

export default function BartenderJobsPage() {
  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'Bartender Jobs — Find Bar Work Near You | BarsConnect',
      description: 'Looking for bartender jobs? Create a free profile on BarsConnect and connect with bars hiring for full-time, part-time, and event shifts. No fees. Free beta access.',
      keywords: 'bartender jobs, bar work, bartender opportunities, part-time bartender jobs, event bartender jobs',
      canonicalUrl: 'https://barsconnect.com/bartender-jobs',
      robots: 'index,follow',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://barsconnect.com/' },
          { '@type': 'ListItem', position: 2, name: 'Bartender Jobs', item: 'https://barsconnect.com/bartender-jobs' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="bartender-jobs-page">
      <div className="max-w-5xl mx-auto">
        <header className="mb-10" data-testid="bartender-jobs-hero-section">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="bartender-jobs-page-title">Bartender Jobs, Found Where You Actually Work</h1>
          <p className="text-muted-foreground mb-4" data-testid="bartender-jobs-hero-copy-1">
            Finding bartender work should not mean scrolling through generic job boards that do not understand hospitality. BarsConnect is built for bartenders, so you can present your skills, schedule, and experience clearly.
          </p>
          <p className="text-muted-foreground mb-6" data-testid="bartender-jobs-hero-copy-2">
            Create a free bartender profile, show your availability, and connect with bars through direct platform messaging. No application fees. No credit card required during beta.
          </p>
          <Button asChild data-testid="bartender-jobs-primary-cta-btn">
            <Link to="/register/bartenders">Create Free Bartender Profile</Link>
          </Button>
        </header>

        <section className="mb-10" data-testid="bartender-jobs-how-it-works-section">
          <h2 className="text-2xl font-semibold text-white mb-4" data-testid="bartender-jobs-how-it-works-heading">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-white/5 border border-white/10" data-testid="bartender-jobs-step-1-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="bartender-jobs-step-1-title">Step 1 — Build Your Profile</h3>
                <p className="text-sm text-muted-foreground" data-testid="bartender-jobs-step-1-copy">
                  Add your experience, preferred work type, and availability so bars can understand what you are looking for.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10" data-testid="bartender-jobs-step-2-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="bartender-jobs-step-2-title">Step 2 — Get Discovered by Bars</h3>
                <p className="text-sm text-muted-foreground" data-testid="bartender-jobs-step-2-copy">
                  Bar owners can browse your profile details and contact you directly when your background fits their needs.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10" data-testid="bartender-jobs-step-3-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="bartender-jobs-step-3-title">Step 3 — Connect and Work</h3>
                <p className="text-sm text-muted-foreground" data-testid="bartender-jobs-step-3-copy">
                  Review messages, discuss shift details, and decide which opportunities are a fit for you.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-10" data-testid="bartender-jobs-usage-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="bartender-jobs-usage-heading">What Bartenders Use BarsConnect For</h2>
          <p className="text-muted-foreground mb-4" data-testid="bartender-jobs-usage-copy-1">
            BarsConnect supports different working styles, including full-time roles, flexible schedules, and one-off event work. Your profile stays visible so bars can reach out when shifts open.
          </p>
          <p className="text-muted-foreground" data-testid="bartender-jobs-usage-copy-2">
            Explore options for <Link className="text-primary hover:underline" to="/part-time-bartender-jobs" data-testid="bartender-jobs-inline-link-part-time">part-time bartender jobs</Link>,
            {' '}<Link className="text-primary hover:underline" to="/event-bartender-jobs" data-testid="bartender-jobs-inline-link-event">event bartender jobs</Link>,
            {' '}and opportunities <Link className="text-primary hover:underline" to="/bartender-jobs-near-me" data-testid="bartender-jobs-inline-link-near-me">near you</Link>.
          </p>
        </section>

        <section className="mb-10" data-testid="bartender-jobs-profile-value-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="bartender-jobs-profile-value-heading">Build a Profile That Works for You</h2>
          <p className="text-muted-foreground mb-4" data-testid="bartender-jobs-profile-value-copy-1">
            Your BarsConnect profile is a live summary of your skills, preferences, and availability. A complete profile helps bar owners decide quickly whether to contact you.
          </p>
          <p className="text-muted-foreground" data-testid="bartender-jobs-profile-value-copy-2">
            Some profiles include a Verified Bartender label, which can add additional trust when owners review multiple candidates.
          </p>
        </section>

        <section className="mb-10" data-testid="bartender-jobs-trust-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="bartender-jobs-trust-heading">Trust and Credibility</h2>
          <p className="text-muted-foreground" data-testid="bartender-jobs-trust-copy">
            BarsConnect is currently in free beta. Bartenders and bar owners can sign up and use core platform features without subscription fees.
          </p>
        </section>

        <section className="mb-10" data-testid="bartender-jobs-faq-section">
          <h2 className="text-2xl font-semibold text-white mb-4" data-testid="bartender-jobs-faq-heading">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((item, index) => (
              <Card key={item.question} className="bg-white/5 border border-white/10" data-testid={`bartender-jobs-faq-card-${index + 1}`}>
                <CardContent className="p-5">
                  <h3 className="text-white font-semibold mb-2" data-testid={`bartender-jobs-faq-question-${index + 1}`}>{item.question}</h3>
                  <p className="text-sm text-muted-foreground" data-testid={`bartender-jobs-faq-answer-${index + 1}`}>{item.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="mb-10" data-testid="bartender-jobs-topical-links-section">
          <h2 className="text-2xl font-semibold text-white mb-4" data-testid="bartender-jobs-topical-links-heading">Explore Bartender Job Guides</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button asChild variant="outline" data-testid="bartender-jobs-topical-link-near-me-btn">
              <Link to="/bartender-jobs-near-me">Bartender Jobs Near Me</Link>
            </Button>
            <Button asChild variant="outline" data-testid="bartender-jobs-topical-link-part-time-btn">
              <Link to="/part-time-bartender-jobs">Part-Time Bartender Jobs</Link>
            </Button>
            <Button asChild variant="outline" data-testid="bartender-jobs-topical-link-event-btn">
              <Link to="/event-bartender-jobs">Event Bartender Jobs</Link>
            </Button>
            <Button asChild variant="outline" data-testid="bartender-jobs-topical-link-app-btn">
              <Link to="/bartender-app">BarsConnect Bartender App</Link>
            </Button>
          </div>
        </section>

        <section className="mb-10" data-testid="bartender-jobs-city-directory-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="bartender-jobs-city-directory-heading">Browse Bartender Jobs by City</h2>
          <p className="text-muted-foreground mb-6" data-testid="bartender-jobs-page-subtitle">
            Explore city-specific landing pages and discover local shift opportunities.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="bartender-jobs-city-list">
            {cities.map((item) => (
              <Card key={item.slug} className="bg-white/5 border border-white/10" data-testid={`bartender-jobs-city-card-${item.city}`}>
                <CardContent className="p-5">
                  <h3 className="text-xl font-semibold text-white mb-1" data-testid={`bartender-jobs-city-title-${item.city}`}>{item.city}</h3>
                  <p className="text-sm text-muted-foreground mb-3" data-testid={`bartender-jobs-city-description-${item.city}`}>{item.description}</p>
                  <Button asChild variant="outline" data-testid={`bartender-jobs-city-link-${item.city}`}>
                    <Link to={item.slug}>View Jobs in {item.city}</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="mb-4" data-testid="bartender-jobs-repeat-cta-section">
          <Card className="bg-white/5 border border-white/10" data-testid="bartender-jobs-repeat-cta-card">
            <CardContent className="p-6 sm:p-8">
              <h2 className="text-2xl font-semibold text-white mb-3" data-testid="bartender-jobs-repeat-cta-heading">Ready to connect with bars that are actively hiring?</h2>
              <p className="text-muted-foreground mb-5" data-testid="bartender-jobs-repeat-cta-copy">
                Your profile is free to create and takes only a few minutes to set up.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild data-testid="bartender-jobs-repeat-cta-register-btn">
                  <Link to="/register/bartenders">Create Free Bartender Profile</Link>
                </Button>
                <Button asChild variant="outline" data-testid="bartender-jobs-repeat-cta-app-btn">
                  <Link to="/bartender-app">Explore the BarsConnect App Experience</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
